import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Route } from 'react-router-dom';

import { Admin, Resource, CustomRoutes } from 'react-admin';

// Styles
import './styles/index.css';

// Data Provider (hibrit: events-v2/stocks -> API, gerisi Firebase)
import dataProvider from './services/dataProvider';

// Resources
import { PaymentShow } from './resources/payments';

import { EventCreate, EventEdit, EventShow } from './resources/events';
import { EventList } from './resources/events/list';
import ActiveEvents from './pages/active-events';
import PastEvents from './pages/past-events';
import PushNotifications from './pages/push-notifications';
import FeaturedVenuesOrder from './pages/featured-venues-order';

// Event-v2
import { EventV2Create } from './resources/events-v2/create';
import EventV2List from './resources/events-v2/list';
import { EventV2Edit } from './resources/events-v2/edit';
import EventV2Show from './resources/events-v2/show';

import { VenueCreate, VenueList, VenueEdit } from './resources/venues';
import { UserEdit } from './resources/users';
import { CityCreate, CityList, CityEdit } from './resources/cities';
import {
  OrganisationCompanyCreate,
  OrganisationCompanyList,
  OrganisationCompanyEdit,
} from './resources/organisationCompany';
import { SalePointCreate, SalePointList, SalePointEdit } from './resources/salePoints';
import { AffiliatesCreate, AffiliatesList, AffiliatesEdit } from './resources/affiliates';
import { SaleAgentCreate, SaleAgentList, SaleAgentEdit } from './resources/saleAgents';
import { PromoterCreate, PromoterEdit, PromoterList } from './resources/promoters';
import { GroupEventCreate, GroupEventList, GroupEventEdit } from './resources/groupEvents';

import { FeaturedList, FeaturedEdit } from './resources/featureds';
import { CategoryCreate, CategoryList, CategoryEdit } from './resources/categories';
import { SubCategoryCreate, SubCategoryList, SubCategoryEdit } from './resources/subcategories';
import {
  VenueCategoryCreate,
  VenueCategoryList,
  VenueCategoryEdit,
} from './resources/venueCategories';
import { EventTagsCreate, EventTagsList, EventTagsEdit } from './resources/eventTags';
import { LayoutCreate, LayoutList, LayoutEdit } from './resources/layouts';
import { SystemParameterList, SystemParameterEdit } from './resources/systemParameters';
import { AppVersionConfigList, AppVersionConfigEdit } from './resources/appVersionConfigs';

import { HomeSectionCreate, HomeSectionList, HomeSectionEdit } from './resources/homeSections';
import { HeroCreate, HeroList, HeroEdit } from './resources/heros';
import { PageCreate, PageList, PageEdit } from './resources/pages';
import { NewsCreate, NewsEdit, NewsList } from './resources/news';
import { ScriptCreate, ScriptEdit, ScriptList } from './resources/scripts';
import {
  SettlementCategoryCreate,
  SettlementCategoryEdit,
  SettlementCategoryList,
} from './resources/settlementCategories';
import { DeleteUserRequestList } from './resources/deleteUserRequests';
import { RefundRequestList } from './resources/refundRequests';

import { StockEdit } from './resources/stocks/edit';

import { AffiliateFormEdit, AffiliateFormList } from './resources/affiliateForm';
import {
  EventCreationApplicationEdit,
  EventCreationApplicationShow,
} from './resources/eventCreationApplications';
import { AdPackageCreate, AdPackageEdit, AdPackageList } from './resources/adPackages';
import {
  AdApplicationEdit,
  AdApplicationList,
  AdApplicationShow,
} from './resources/adApplications';
import { SessionEventList } from './resources/sessionEvents';

import { LogList } from './resources/verificationLogs';
import { SaleLogList } from './resources/saleLogs';
import { EventLogList } from './resources/eventLogs';

// Custom pages
import Dashboard from './pages/dashboard';
import SalesPage from './pages/sales';
import TicketsPage from './pages/tickets';
import UsersPage from './pages/users';
import StocksPage from './pages/stocks';
import SaleReport from './pages/sale-report';
import SeatOpsPage from './pages/seat-ops';
import PendingEventsPage from './pages/pending-events';
import MagicLoginPage from './pages/magic-login';
import DeepLSettings from './pages/deepl-settings';
import {
  LayoutsV2List,
  LayoutsV2Create,
  LayoutsV2Edit,
  LayoutsV2View,
  LayoutsV2Shell,
} from './layouts-v2';

// i18n
import { i18nProvider } from './i18n/i18nProvider';

// Components
import CustomLayout from './components/layout/CustomLayout';
import ErrorBoundary from './app/ErrorBoundary.jsx';

// Theme
import theme from './constants/theme';
import RequireAuth from './app/RequireAuth.jsx';
import RequireAdmin from './app/RequireAdmin.jsx';
import { customRoutes } from './app/routes';
import customAuthProvider from './services/authProvider';

const App = () => {
  const [role, setRole] = useState(null);

  useEffect(() => {
    const fetchRole = async () => {
      try {
        const claims = await customAuthProvider.getPermissions();
        if (claims && claims.crole) setRole(claims.crole);
      } catch (error) {
        console.error('Error fetching role', error);
      }
    };

    fetchRole();
  }, []);

  return (
    <Admin
      theme={theme}
      layout={CustomLayout}
      authProvider={customAuthProvider}
      dataProvider={dataProvider}
      i18nProvider={i18nProvider}
      title="Gişe Kıbrıs"
      requireAuth
      ready={role !== null}
    >
      {role === 'editor' ? (
        <Resource name="news" list={NewsList} create={NewsCreate} edit={NewsEdit} />
      ) : (
        <React.Fragment>
          <Resource
            name="events-v2"
            list={EventV2List}
            create={EventV2Create}
            edit={EventV2Edit}
            show={EventV2Show}
          />
          <Resource
            name="events"
            list={EventList}
            edit={EventEdit}
            create={EventCreate}
            show={EventShow}
          />
          <Resource name="dashboard" list={Dashboard} />
          <Resource name="sales" list={SalesPage} />
          <Resource name="tickets" list={TicketsPage} />
          <Resource name="payments" show={PaymentShow} />
          <Resource name="users" list={UsersPage} edit={UserEdit} />
          <Resource name="sessionEvents" list={SessionEventList} />
          <Resource name="venues" list={VenueList} create={VenueCreate} edit={VenueEdit} />
          <Resource name="layouts" list={LayoutList} create={LayoutCreate} edit={LayoutEdit} />
          <Resource name="cities" list={CityList} create={CityCreate} edit={CityEdit} />
          <Resource
            name="organisationCompanies"
            list={OrganisationCompanyList}
            create={OrganisationCompanyCreate}
            edit={OrganisationCompanyEdit}
          />
          <Resource
            name="salePoints"
            list={SalePointList}
            create={SalePointCreate}
            edit={SalePointEdit}
          />
          <Resource
            name="saleAgents"
            list={SaleAgentList}
            create={SaleAgentCreate}
            edit={SaleAgentEdit}
          />
          <Resource
            name="promoters"
            list={PromoterList}
            create={PromoterCreate}
            edit={PromoterEdit}
          />
          <Resource
            name="affiliates"
            list={AffiliatesList}
            create={AffiliatesCreate}
            edit={AffiliatesEdit}
          />
          <Resource
            name="groupEvents"
            list={GroupEventList}
            create={GroupEventCreate}
            edit={GroupEventEdit}
          />
          <Resource name="affiliateForm" list={AffiliateFormList} edit={AffiliateFormEdit} />
          <Resource
            name="eventCreationApplications"
            show={EventCreationApplicationShow}
            edit={EventCreationApplicationEdit}
          />
          <Resource
            name="adPackages"
            list={AdPackageList}
            create={AdPackageCreate}
            edit={AdPackageEdit}
          />
          <Resource
            name="adApplications"
            list={AdApplicationList}
            show={AdApplicationShow}
            edit={AdApplicationEdit}
          />
          <Resource name="featureds" list={FeaturedList} edit={FeaturedEdit} />
          <Resource
            name="categories"
            list={CategoryList}
            create={CategoryCreate}
            edit={CategoryEdit}
          />
          <Resource
            name="subcategories"
            list={SubCategoryList}
            create={SubCategoryCreate}
            edit={SubCategoryEdit}
          />
          <Resource
            name="venueCategories"
            list={VenueCategoryList}
            create={VenueCategoryCreate}
            edit={VenueCategoryEdit}
          />
          <Resource
            name="eventTags"
            list={EventTagsList}
            create={EventTagsCreate}
            edit={EventTagsEdit}
          />
          <Resource name="deleteUserRequests" list={DeleteUserRequestList} />
          <Resource name="refundRequests" list={RefundRequestList} />
          <Resource name="systemParameters" list={SystemParameterList} edit={SystemParameterEdit} />
          <Resource
            name="appVersionConfigs"
            list={AppVersionConfigList}
            edit={AppVersionConfigEdit}
          />
          <Resource
            name="homeSections"
            list={HomeSectionList}
            create={HomeSectionCreate}
            edit={HomeSectionEdit}
          />
          <Resource
            name="heros"
            list={HeroList}
            create={HeroCreate}
            edit={HeroEdit}
            options={{ label: 'Home Special Event Hero' }}
          />
          <Resource name="pages" list={PageList} create={PageCreate} edit={PageEdit} />
          <Resource name="news" list={NewsList} create={NewsCreate} edit={NewsEdit} />
          <Resource name="scripts" list={ScriptList} create={ScriptCreate} edit={ScriptEdit} />
          <Resource
            name="settlementCategories"
            list={SettlementCategoryList}
            create={SettlementCategoryCreate}
            edit={SettlementCategoryEdit}
            options={{ label: 'Accountancy Items' }}
          />
          <Resource name="eventLogs" list={EventLogList} />
          <Resource name="verificationLogs" list={LogList} />
          <Resource name="paymentLogs" list={SaleLogList} />
          <Resource name="stocks" list={StocksPage} edit={StockEdit} />

          <CustomRoutes>
            <Route
              path={customRoutes.ACTIVE_EVENTS}
              element={
                <RequireAuth>
                  <ActiveEvents />
                </RequireAuth>
              }
            />
            <Route
              path={customRoutes.PAST_EVENTS}
              element={
                <RequireAuth>
                  <PastEvents />
                </RequireAuth>
              }
            />
            <Route
              path={customRoutes.SALE_REPORT}
              element={
                <RequireAuth>
                  <SaleReport />
                </RequireAuth>
              }
            />
            <Route
              path={customRoutes.PUSH_NOTIFICATIONS}
              element={
                <RequireAuth>
                  <PushNotifications />
                </RequireAuth>
              }
            />
            <Route
              path={customRoutes.FEATURED_VENUES_ORDER}
              element={
                <RequireAuth>
                  <FeaturedVenuesOrder />
                </RequireAuth>
              }
            />
            <Route
              path={customRoutes.SEAT_OPS}
              element={
                <RequireAuth>
                  <SeatOpsPage />
                </RequireAuth>
              }
            />
            <Route
              path={customRoutes.PENDING_EVENTS}
              element={
                <RequireAuth>
                  <PendingEventsPage />
                </RequireAuth>
              }
            />
            <Route
              path={customRoutes.DEEPL_SETTINGS}
              element={
                <RequireAuth>
                  <RequireAdmin>
                    <DeepLSettings />
                  </RequireAdmin>
                </RequireAuth>
              }
            />
          </CustomRoutes>
          <CustomRoutes noLayout>
            <Route path={customRoutes.MAGIC_LOGIN} element={<MagicLoginPage />} />
            <Route
              path={customRoutes.LAYOUTS_V2}
              element={
                <RequireAuth>
                  <LayoutsV2Shell title="Layouts">
                    <LayoutsV2List />
                  </LayoutsV2Shell>
                </RequireAuth>
              }
            />
            <Route
              path={customRoutes.LAYOUTS_V2_CREATE}
              element={
                <RequireAuth>
                  <div className="layouts-v2-root layouts-v2-shell">
                    <LayoutsV2Create />
                  </div>
                </RequireAuth>
              }
            />
            <Route
              path={customRoutes.LAYOUTS_V2_VIEW}
              element={
                <RequireAuth>
                  <LayoutsV2Shell title="View Layout">
                    <LayoutsV2View />
                  </LayoutsV2Shell>
                </RequireAuth>
              }
            />
            <Route
              path={customRoutes.LAYOUTS_V2_EDIT}
              element={
                <RequireAuth>
                  <LayoutsV2Shell title="Edit Layout">
                    <LayoutsV2Edit />
                  </LayoutsV2Shell>
                </RequireAuth>
              }
            />
          </CustomRoutes>
        </React.Fragment>
      )}
    </Admin>
  );
};

const root = createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>,
);
